import {
  CREATE_FOLDER,
  UPDATE_FOLDER,
  DELETE_FOLDER,
  ADD_DOCUMENT_TO_FOLDER,
  REMOVE_DOCUMENT_FROM_FOLDER,
  SELECT_DOCUMENT,
  SYNC_FOLDERS,
  REFRESH_FOLDERS,
  FREE_DOCUMENTS_FROM_FOLDER
} from '../../actions/types';

// Funzione di utilità per ordinare le cartelle per data di creazione
const sortFoldersByDate = (folders) => {
  return [...folders].sort((a, b) => {
    // Gestisci il caso in cui createdAt potrebbe mancare
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    
    // Converti le stringhe in oggetti Date se necessario
    const dateA = typeof a.createdAt === 'string' ? new Date(a.createdAt) : a.createdAt;
    const dateB = typeof b.createdAt === 'string' ? new Date(b.createdAt) : b.createdAt;
    
    // Ordina in ordine decrescente (più recenti prima)
    return dateB - dateA;
  });
};

// Funzioni di utilità per gestire ID cartelle e sottocartelle
const isSubfolder = (folderId) => {
  if (!folderId) return false;
  return folderId.startsWith('subfolder_');
};

const getParentIdFromSubfolder = (subfolderId) => {
  if (!isSubfolder(subfolderId)) return null;
  
  const parts = subfolderId.split('_parent_');
  if (parts.length < 2) {
    console.error("Formato ID sottocartella non valido:", subfolderId);
    return null;
  }
  
  const parentId = parts[1];
  // Rimuovi il prefisso 'firebase_' se presente
  return parentId.startsWith('firebase_') ? parentId.replace('firebase_', '') : parentId;
};

const findFolderById = (folders, folderId) => {
  if (!folderId || !folders || !Array.isArray(folders)) return null;
  
  // Normalizza l'ID rimuovendo eventuali prefissi
  const normalizedId = folderId.startsWith('firebase_') ? folderId.replace('firebase_', '') : folderId;
  
  // Cerca la cartella con qualsiasi formato dell'ID
  return folders.find(folder => 
    folder.id === folderId || 
    folder.id === normalizedId || 
    folder.id === `firebase_${normalizedId}`
  );
};

/**
 * Collegamenti documento -> cartella in attesa di conferma dal server.
 *
 * All'upload dentro una cartella il documento viene creato PRIMA e agganciato
 * DOPO, con una seconda scrittura dal client. Nel mezzo lo store puo' ricevere
 * il documento (fetch della libreria) e la lista cartelle ancora vecchia: il
 * documento comparirebbe per un istante nei "Recenti" della sidebar — che ora
 * mostrano solo i documenti FUORI dalle cartelle — per poi sparire. Qui si
 * tiene memoria del collegamento finche' la lettura da Firebase non lo
 * conferma, cosi' il collegamento c'e' gia' quando il documento appare.
 */
const rememberPendingLink = (pending, documentId, folderId) => ({
  ...(pending || {}),
  [documentId]: folderId
});

const dropPendingLinks = (state, documentIds) => {
  const ids = (Array.isArray(documentIds) ? documentIds : [documentIds]).filter(Boolean);
  const pending = state.pendingFolderLinks;
  if (!pending || !ids.length || !ids.some(id => pending[id])) return state;
  const next = { ...pending };
  ids.forEach(id => { delete next[id]; });
  return { ...state, pendingFolderLinks: next };
};

/**
 * Riapplica i collegamenti in attesa sulle cartelle appena lette da Firebase.
 * Un collegamento sparisce dall'attesa quando il server lo conferma (l'id e'
 * gia' nella cartella) o quando la cartella non esiste piu'. Se la scrittura
 * su Firebase e' fallita il collegamento resta ottimistico per la sessione,
 * come gia' fa il resto del flusso (`localSuccess`).
 */
const applyPendingLinks = (folders, pending) => {
  const entries = Object.entries(pending || {});
  if (!entries.length) return { folders, pendingFolderLinks: pending || {} };

  const stillPending = {};
  const toAdd = new Map(); // id cartella reale -> Set di documentId

  entries.forEach(([documentId, folderId]) => {
    const folder = findFolderById(folders, folderId);
    if (!folder) return; // cartella sparita: niente da proteggere
    if ((folder.documentIds || []).includes(documentId)) return; // confermato
    stillPending[documentId] = folderId;
    if (!toAdd.has(folder.id)) toAdd.set(folder.id, new Set());
    toAdd.get(folder.id).add(documentId);
  });

  if (!toAdd.size) return { folders, pendingFolderLinks: stillPending };

  return {
    folders: folders.map(folder => {
      const missing = toAdd.get(folder.id);
      if (!missing) return folder;
      return {
        ...folder,
        documentIds: [...(folder.documentIds || []), ...missing]
      };
    }),
    pendingFolderLinks: stillPending
  };
};

// Fusione intelligente delle cartelle locali e quelle caricate da Firebase
const mergeFolders = (localFolders, firebaseFolders) => {
  // Se non ci sono cartelle Firebase, restituisci solo quelle locali
  if (!firebaseFolders || !Array.isArray(firebaseFolders) || firebaseFolders.length === 0) {
    return [...localFolders];
  }
  
  // Se non ci sono cartelle locali, restituisci solo quelle Firebase
  if (!localFolders || !Array.isArray(localFolders) || localFolders.length === 0) {
    return [...firebaseFolders];
  }
  
  // Crea un Map delle cartelle Firebase per lookup efficiente
  // La chiave è una combinazione di nome e parentId per gestire cartelle con lo stesso nome in posizioni diverse
  const firebaseFolderMap = new Map();
  
  firebaseFolders.forEach(folder => {
    const key = `${(folder.name || '').toLowerCase()}_${folder.parentId || 'root'}`;
    firebaseFolderMap.set(key, true);
  });
  
  // Filtra le cartelle locali che non esistono su Firebase
  const uniqueLocalFolders = localFolders.filter(localFolder => {
    // Considera solo cartelle locali (che hanno un ID che inizia con 'local_')
    if (!localFolder.id || !localFolder.id.startsWith('local_')) {
      return false;
    }
    
    // Crea la stessa chiave di ricerca
    const key = `${(localFolder.name || '').toLowerCase()}_${localFolder.parentId || 'root'}`;
    
    // Se la cartella esiste già su Firebase, non aggiungerla
    return !firebaseFolderMap.has(key);
  });
  
  // Combina le cartelle Firebase con le cartelle locali uniche
  return [...firebaseFolders, ...uniqueLocalFolders];
};

// Handler per le azioni relative alle cartelle
export const handleSelectDocument = (state, payload) => {
  return {
    ...state,
    selectedDocument: payload,
  };
};

export const handleSyncFolders = (state, payload) => {
  // Verifica che action.payload sia un array valido
  if (!payload || !Array.isArray(payload)) {
    console.error('SYNC_FOLDERS: payload non valido', payload);
    return state;
  }
  
  console.log('Sincronizzazione cartelle da Firebase', payload.length);
  // La fusione tiene le cartelle del server e butta quelle locali: senza
  // riapplicare i collegamenti in attesa, un aggancio ancora in volo verso
  // Firestore verrebbe cancellato da questa lettura.
  const merged = applyPendingLinks(
    mergeFolders(state.folders, payload),
    state.pendingFolderLinks
  );
  return {
    ...state,
    folders: sortFoldersByDate(merged.folders),
    pendingFolderLinks: merged.pendingFolderLinks
  };
};

export const handleRefreshFolders = (state) => {
  console.log('Forzando refresh delle cartelle nello store Redux');
  // Forza un nuovo array per triggerare aggiornamenti React
  // Creiamo un deep clone delle cartelle per garantire nuovi riferimenti
  // e applichiamo l'ordinamento per data
  return {
    ...state,
    folders: sortFoldersByDate(state.folders.map(folder => {
      // Deep clone della cartella
      const clonedFolder = {...folder};
      
      // Deep clone delle sottocartelle se presenti
      if (Array.isArray(folder.subfolders) && folder.subfolders.length > 0) {
        console.log(`Aggiornamento sottocartelle per ${folder.name} (ID: ${folder.id})`);
        clonedFolder.subfolders = folder.subfolders.map(subfolder => ({...subfolder}));
        console.log(`Sottocartelle aggiornate (${clonedFolder.subfolders.length}): `, 
          clonedFolder.subfolders.map(sf => sf.name || sf.id).join(', '));
      } else if (folder.subfolders && !Array.isArray(folder.subfolders)) {
        // Correggi se subfolders non è un array
        console.warn(`Correzione formato non-array di subfolders per la cartella ${folder.id}`);
        clonedFolder.subfolders = [];
      } else {
        console.log(`Nessuna sottocartella trovata per ${folder.name || folder.id}`);
      }
      
      return clonedFolder;
    }))
  };
};

// Handler per l'azione CREATE_FOLDER
export const handleCreateFolder = (state, payload) => {
  console.log('Creazione cartella nel reducer:', payload);
  
  // Validazione dell'input
  if (!payload || typeof payload !== 'object') {
    console.error('Payload non valido per CREATE_FOLDER:', payload);
    return state;
  }
  
  // Verifica che i campi obbligatori siano presenti
  const { id, name, parentId } = payload;
  if (!id || !name) {
    console.error('Payload incompleto per CREATE_FOLDER:', payload);
    return state;
  }
  
  // Verifica che non ci siano cartelle con lo stesso ID
  const existingFolder = state.folders.find(folder => folder.id === id);
  if (existingFolder) {
    console.error(`Cartella con ID ${id} già esistente`);
    return state;
  }
  
  // Prepara il payload con valori di default per campi opzionali
  const folderPayload = {
    ...payload,
    documentIds: Array.isArray(payload.documentIds) ? payload.documentIds : [],
    createdAt: payload.createdAt || new Date().toISOString(),
    color: payload.color || 'blue'
  };
  
  // Se è una sottocartella, aggiorna la relazione con il genitore
  if (parentId) {
    // Cerca la cartella genitore
    const parentFolder = findFolderById(state.folders, parentId);
    
    if (!parentFolder) {
      console.error(`Cartella genitore con ID ${parentId} non trovata per la creazione della sottocartella`);
      // Creiamo comunque la cartella, ma senza relazione genitore-figlio
      return {
        ...state,
        folders: sortFoldersByDate([...state.folders, folderPayload])
      };
    }
    
    // Log per debug
    console.log(`Creazione cartella "${name}" dentro cartella genitore "${parentFolder.name}" (ID: ${parentId})`);
    
    return {
      ...state,
      folders: sortFoldersByDate([
        // Aggiungiamo la nuova cartella all'array principale
        ...state.folders, 
        folderPayload,
        // Aggiorniamo la cartella genitore
      ].map(folder => {
        if (folder.id === parentId) {
          // Aggiungiamo la relazione in subfolders
          const subfolders = Array.isArray(folder.subfolders) ? folder.subfolders : [];
          
          console.log(`Aggiornando sottocartelle per "${folder.name}":`, 
            [...subfolders.map(sf => sf.name), name].join(', '));
          
          return {
            ...folder,
            subfolders: [...subfolders, {
              id: id,
              name: name,
              documentIds: folderPayload.documentIds,
              createdAt: folderPayload.createdAt,
              color: folderPayload.color,
              parentId: parentId
            }]
          };
        }
        return folder;
      }))
    };
  }
  
  // Cartella di primo livello
  console.log(`Creazione cartella di primo livello: "${name}"`);
  return {
    ...state,
    folders: sortFoldersByDate([...state.folders, folderPayload])
  };
};

// Handler per l'azione DELETE_FOLDER
export const handleDeleteFolder = (state, payload) => {
  // Utilizza le funzioni di utilità per trovare la cartella
  const folderToDelete = findFolderById(state.folders, payload);
  
  // Se la cartella non esiste, rimuovi solo dall'array di cartelle
  if (!folderToDelete) {
    console.log(`Cartella con ID ${payload} non trovata, rimuoviamo solo dall'array`);
    return {
      ...state,
      folders: state.folders.filter(folder => 
        folder.id !== payload && 
        folder.id !== `firebase_${payload}` &&
        `firebase_${folder.id}` !== payload
      )
    };
  }
  
  // Nota: i documenti contenuti nella cartella devono essere già stati liberati
  // dall'azione FREE_DOCUMENTS_FROM_FOLDER che dovrebbe essere chiamata prima di DELETE_FOLDER
  
  // Gestione diversa per sottocartelle
  if (isSubfolder(folderToDelete.id)) {
    const parentId = getParentIdFromSubfolder(folderToDelete.id);
    if (parentId) {
      console.log(`Eliminando sottocartella ${folderToDelete.id} con genitore ${parentId}`);
      
      return {
        ...state,
        // Rimuovi la cartella dall'array
        folders: state.folders.filter(folder => folder.id !== folderToDelete.id)
          .map(folder => {
            // Aggiorna la struttura della cartella genitore se esiste
            const isParent = folder.id === parentId || 
                          folder.id === `firebase_${parentId}` ||
                          `firebase_${folder.id}` === parentId;
            
            if (isParent && folder.subfolders) {
              // Rimuovi la sottocartella dall'array subfolders del genitore
              return {
                ...folder,
                subfolders: folder.subfolders.filter(sf => sf.id !== folderToDelete.id)
              };
            }
            return folder;
          })
      };
    }
  }
  
  // Per cartelle principali, semplicemente rimuoviamo dall'array di cartelle
  console.log(`Eliminando cartella principale ${folderToDelete.id}`);
  return {
    ...state,
    folders: state.folders.filter(folder => folder.id !== folderToDelete.id)
  };
};

// Handler per l'azione ADD_DOCUMENT_TO_FOLDER
export const handleAddDocumentToFolder = (state, payload) => {
  const { folderId, documentId } = payload;

  if (!folderId || !documentId) {
    console.error('ADD_DOCUMENT_TO_FOLDER: payload non valido', payload);
    return state;
  }

  // Qui c'era un controllo che scartava il collegamento se il documento non
  // era ANCORA in `state.documents`. All'upload e' esattamente quello che
  // succede: il documento viene scritto su Firestore e la cartella viene
  // agganciata subito dopo, mentre lo store riceve il documento solo al
  // successivo fetch della libreria. Il collegamento andava perso e il
  // documento compariva un istante nei "Recenti" (che ora mostrano solo i
  // documenti fuori dalle cartelle) per poi sparire al refresh delle cartelle.
  // La cartella tollera gia' id di documenti non caricati: la sidebar li
  // risolve con `documentsById` e chiede i mancanti a Firestore.

  // Crea un nuovo array di cartelle con il documento aggiunto alla cartella specificata
  const updatedFolders = state.folders.map(folder => {
    // Se è questa la cartella alla quale dobbiamo aggiungere il documento
    if (folder.id === folderId) {
      // Se la cartella ha già un array documentIds, aggiungi il nuovo documento
      // altrimenti crea un nuovo array con il documento
      const existingDocIds = folder.documentIds || [];
      
      // Se il documento è già presente, non fare nulla
      if (existingDocIds.includes(documentId)) {
        console.log(`Documento ${documentId} già presente nella cartella ${folderId}`);
        return folder;
      }
      
      const updatedFolder = {
        ...folder,
        documentIds: [...existingDocIds, documentId]
      };
      
      console.log(`Documento ${documentId} aggiunto alla cartella ${folderId} in Redux`);
      console.log(`Nuovi documentIds: ${updatedFolder.documentIds.length} documenti`);
      
      return updatedFolder;
    }
    
    return folder;
  });
  
  // Crea un nuovo stato con l'array di cartelle aggiornato
  return {
    ...state,
    folders: updatedFolders,
    // Il collegamento resta segnato finche' non torna dal server: cosi'
    // la prossima lettura delle cartelle non lo cancella (vedi handleSyncFolders)
    pendingFolderLinks: rememberPendingLink(
      state.pendingFolderLinks,
      documentId,
      folderId
    ),
    // Incrementiamo il counter per forzare un aggiornamento dell'UI
    folderRefreshCounter: state.folderRefreshCounter + 1
  };
};

// Handler per l'azione FREE_DOCUMENTS_FROM_FOLDER
export const handleFreeDocumentsFromFolder = (state, payload) => {
  const { folderId, documentIds, includeSubfolders, forceCleanup, finalCleanup } = payload;
  
  // Validazione input: verifica che documentIds sia un array non vuoto
  if (!documentIds || !Array.isArray(documentIds) || documentIds.length === 0) {
    console.error('FREE_DOCUMENTS_FROM_FOLDER: documentIds non valido', payload);
    return state;
  }
  
  // Il finalCleanup è un'operazione speciale che non richiede un folderId valido
  if (!finalCleanup && !folderId) {
    console.error('FREE_DOCUMENTS_FROM_FOLDER: folderId non valido', payload);
    return state;
  }
  
  console.log(`Liberando ${documentIds.length} documenti dalla cartella ${folderId}${includeSubfolders ? ' (incluse sottocartelle)' : ''}${forceCleanup ? ' (pulizia forzata)' : ''}${finalCleanup ? ' (pulizia finale)' : ''}`);
  
  // Se è finalCleanup, elaboriamo direttamente i documenti senza cercare cartelle
  let folderIdsToProcess = finalCleanup ? [] : [folderId];
  let allDocumentIds = [...documentIds];
  
  // Se non è finalCleanup, cerchiamo le cartelle correlate
  if (!finalCleanup) {
    // Verifica che la cartella esista effettivamente nello store
    const folder = state.folders.find(f => 
      f.id === folderId || 
      `firebase_${f.id.replace('firebase_', '')}` === folderId ||
      f.id === `firebase_${folderId.replace('firebase_', '')}`
    );
    
    // Se includeSubfolders è true e la cartella ha sottocartelle, aggiungi anche gli ID delle sottocartelle
    if (includeSubfolders && folder && Array.isArray(folder.subfolders) && folder.subfolders.length > 0) {
      // Aggiungi gli ID delle sottocartelle all'elenco delle cartelle da processare
      folderIdsToProcess = [
        ...folderIdsToProcess,
        ...folder.subfolders.map(sf => sf.id).filter(Boolean)
      ];
      
      // Raccogliamo tutti i documentIds da tutte le sottocartelle
      folder.subfolders.forEach(subfolder => {
        if (subfolder && Array.isArray(subfolder.documentIds)) {
          allDocumentIds = [...allDocumentIds, ...subfolder.documentIds];
        }
      });
      
      // Rimuovi duplicati
      allDocumentIds = [...new Set(allDocumentIds)];
      
      console.log(`Elaborazione estesa a ${folderIdsToProcess.length} cartelle con totale ${allDocumentIds.length} documenti`);
    }
    
    // Approccio più aggressivo: trova tutti i documenti che sono in qualsiasi cartella che corrisponde al pattern
    const allFolderIds = new Set(folderIdsToProcess);
    
    // Per il cleanup finale o forzato, estendi la ricerca
    if (forceCleanup || finalCleanup) {
      // Esplora tutte le cartelle per trovare riferimenti a cartelle in eliminazione
      state.folders.forEach(f => {
        if (allFolderIds.has(f.id)) return; // Ignora le cartelle che stiamo già elaborando
        
        // Cerca nelle sottocartelle per vedere se ci sono coincidenze
        if (Array.isArray(f.subfolders)) {
          f.subfolders.forEach(sf => {
            if (allFolderIds.has(sf.id)) {
              // Aggiungi documenti della sottocartella correlata all'elenco
              if (Array.isArray(sf.documentIds)) {
                allDocumentIds = [...allDocumentIds, ...sf.documentIds];
              }
            }
          });
        }
      });
    }
  }
  
  // Rimuovi duplicati un'ultima volta
  allDocumentIds = [...new Set(allDocumentIds)];
  
  // Aggiorna i documenti: segna i documenti come non più appartenenti a quella cartella
  const updatedDocuments = state.documents.map(doc => {
    if (allDocumentIds.includes(doc.id)) {
      // Creiamo una copia del documento per evitare mutazioni dirette
      const docCopy = { ...doc };
      
      // Se è cleanup finale o forzato, rimuovi completamente tutti i riferimenti
      if (finalCleanup || forceCleanup) {
        delete docCopy.folderId;
        delete docCopy.folderIds;
        console.log(`Documento ${doc.id} completamente liberato da tutte le associazioni`);
        return docCopy;
      }
      
      // Se il documento ha un folderId che corrisponde a una delle cartelle da processare, lo rimuoviamo
      if (folderIdsToProcess.some(fid => 
        docCopy.folderId === fid || 
        `firebase_${docCopy.folderId}` === fid ||
        docCopy.folderId === `firebase_${fid}`
      )) {
        delete docCopy.folderId;
        console.log(`Documento ${doc.id} liberato (folderId rimosso)`);
      }
      
      // Se il documento ha un folderIds array che include la cartella eliminata, lo rimuoviamo
      if (Array.isArray(docCopy.folderIds) && docCopy.folderIds.length > 0) {
        const originalLength = docCopy.folderIds.length;
        docCopy.folderIds = docCopy.folderIds.filter(id => 
          !folderIdsToProcess.some(fid => 
            id === fid || 
            `firebase_${id}` === fid || 
            id === `firebase_${fid}`
          )
        );
        
        if (docCopy.folderIds.length !== originalLength) {
          console.log(`Documento ${doc.id} liberato (rimosso da folderIds array)`);
        }
      }
      
      return docCopy;
    }
    return doc;
  });
  
  // Se è cleanup finale, elimina tutti i riferimenti alle cartelle specificate
  let updatedFolders = state.folders;
  
  if (finalCleanup) {
    // Pulisci completamente tutti i riferimenti
    updatedFolders = updatedFolders.map(folder => {
      let hasChanges = false;
      
      // Pulisci documentIds nei documenti specificati
      if (Array.isArray(folder.documentIds)) {
        const newDocIds = folder.documentIds.filter(id => !allDocumentIds.includes(id));
        if (newDocIds.length !== folder.documentIds.length) {
          hasChanges = true;
          folder = { ...folder, documentIds: newDocIds };
        }
      }
      
      // Pulisci sottocartelle
      if (Array.isArray(folder.subfolders) && folder.subfolders.length > 0) {
        // Pulisci documentIds in ogni sottocartella
        const newSubfolders = folder.subfolders.map(sf => {
          if (Array.isArray(sf.documentIds)) {
            const newSubfolderDocIds = sf.documentIds.filter(id => !allDocumentIds.includes(id));
            if (newSubfolderDocIds.length !== sf.documentIds.length) {
              return { ...sf, documentIds: newSubfolderDocIds };
            }
          }
          return sf;
        });
        
        if (JSON.stringify(newSubfolders) !== JSON.stringify(folder.subfolders)) {
          hasChanges = true;
          folder = { ...folder, subfolders: newSubfolders };
        }
      }
      
      return hasChanges ? folder : folder;
    });
  } else {
    // NUOVO: Rimuovi esplicitamente tutti i riferimenti ai documenti in tutte le cartelle, incluso nelle sottocartelle
    const cleanFolders = folders => {
      return folders.map(folder => {
        let updated = false;
        
        // Pulisci documentIds nella cartella principale
        if (Array.isArray(folder.documentIds) && folder.documentIds.some(id => allDocumentIds.includes(id))) {
          updated = true;
          folder = {
            ...folder,
            documentIds: folder.documentIds.filter(id => !allDocumentIds.includes(id))
          };
        }
        
        // Pulisci documentIds nelle sottocartelle
        if (Array.isArray(folder.subfolders) && folder.subfolders.length > 0) {
          const updatedSubfolders = folder.subfolders.map(subfolder => {
            if (Array.isArray(subfolder.documentIds) && subfolder.documentIds.some(id => allDocumentIds.includes(id))) {
              return {
                ...subfolder,
                documentIds: subfolder.documentIds.filter(id => !allDocumentIds.includes(id))
              };
            }
            return subfolder;
          });
          
          // Se almeno una sottocartella è cambiata, aggiorna le sottocartelle
          if (updatedSubfolders.some((sf, i) => sf !== folder.subfolders[i])) {
            updated = true;
            folder = {
              ...folder,
              subfolders: updatedSubfolders
            };
          }
        }
        
        return updated ? folder : folder;
      });
    };
    
    // Aggiorna anche tutte le altre cartelle per rimuovere riferimenti ai documenti
    
    // Rimuovi prima le cartelle che stiamo eliminando (quelle in folderIdsToProcess)
    if (forceCleanup) {
      updatedFolders = updatedFolders.filter(folder => 
        !folderIdsToProcess.some(id => 
          folder.id === id || 
          `firebase_${folder.id}` === id || 
          folder.id === `firebase_${id}`
        )
      );
    }
    
    // Ora pulisci tutte le rimanenti
    updatedFolders = cleanFolders(updatedFolders);
  }
  
  // Crea un nuovo stato con gli array aggiornati e forza un aggiornamento componenti
  const newState = {
    ...state,
    documents: updatedDocuments,
    folders: updatedFolders,
    // Incrementiamo il counter per forzare un aggiornamento dell'UI
    folderRefreshCounter: state.folderRefreshCounter + 2 // +2 per garantire un aggiornamento forzato
  };
  
  console.log(`Completata liberazione dei documenti: ${allDocumentIds.length} documenti liberati`);
  return newState;
};

// Handler per l'azione REMOVE_DOCUMENT_FROM_FOLDER
export const handleRemoveDocumentFromFolder = (state, payload) => {
  const { folderId, documentId } = payload;
  
  // Utilizziamo le funzioni di utilità per identificare il tipo di cartella
  if (isSubfolder(folderId)) {
    const parentId = getParentIdFromSubfolder(folderId);
    if (!parentId) {
      console.error("ID cartella genitore non trovato per la sottocartella:", folderId);
      return state;
    }
    
    // Cerca la cartella genitore usando la funzione di utilità
    const parentFolder = findFolderById(state.folders, parentId);
    if (!parentFolder) {
      console.error(`Cartella genitore con ID ${parentId} non trovata`);
      return state;
    }
    
    return {
      ...state,
      folders: state.folders.map(folder => {
        // Verifica se questa è la cartella genitore
        if (folder.id === parentFolder.id) {
          const subfolders = folder.subfolders || [];
          
          // Aggiorna la sottocartella
          const updatedSubfolders = subfolders.map(subfolder => {
            if (subfolder.id === folderId) {
              const documentIds = subfolder.documentIds || [];
              return {
                ...subfolder,
                documentIds: documentIds.filter(id => id !== documentId)
              };
            }
            return subfolder;
          });
          
          return {
            ...folder,
            subfolders: updatedSubfolders
          };
        }
        return folder;
      })
    };
  }
  
  // Cartella di primo livello (comportamento esistente)
  // ma verifichiamo che documentIds sia un array
  return {
    ...state,
    folders: state.folders.map(folder => {
      if (folder.id === folderId) {
        // Aggiungiamo un controllo per assicurarci che documentIds sia un array
        const documentIds = Array.isArray(folder.documentIds) ? 
          folder.documentIds : [];
          
        return {
          ...folder,
          documentIds: documentIds.filter(id => id !== documentId)
        };
      }
      return folder;
    })
  };
};

// Handler per l'azione UPDATE_FOLDER
export const handleUpdateFolder = (state, payload) => {
  const { folderId, updates } = payload;
  console.log(`Aggiornamento cartella ${folderId} nel reducer:`, updates);
  
  // Utilizza la funzione isSubfolder per verificare se è una sottocartella
  if (isSubfolder(folderId)) {
    const parentId = getParentIdFromSubfolder(folderId);
    if (!parentId) {
      console.error("ID cartella genitore non trovato per la sottocartella:", folderId);
      return state;
    }
    
    // Usa findFolderById per trovare la cartella genitore
    const parentFolder = findFolderById(state.folders, parentId);
    if (!parentFolder) {
      console.error(`Cartella genitore con ID ${parentId} non trovata`);
      return state;
    }
    
    return {
      ...state,
      folders: state.folders.map(folder => {
        // Verifica se questa è la cartella genitore
        if (folder.id === parentFolder.id) {
          const subfolders = folder.subfolders || [];
          
          // Aggiorna la sottocartella
          const updatedSubfolders = subfolders.map(subfolder => {
            if (subfolder.id === folderId) {
              // Se ci sono documenti da rimuovere, li filtriamo
              if (updates.removeDocumentIds && Array.isArray(updates.removeDocumentIds) && updates.removeDocumentIds.length > 0) {
                const updatedDocumentIds = Array.isArray(subfolder.documentIds) ?
                  subfolder.documentIds.filter(id => !updates.removeDocumentIds.includes(id)) :
                  [];
                
                return {
                  ...subfolder,
                  ...updates,
                  documentIds: updatedDocumentIds
                };
              }
              // Se ci sono documentIds espliciti, li utilizziamo direttamente
              else if (updates.documentIds) {
                return {
                  ...subfolder,
                  ...updates
                };
              }
              // Altrimenti, aggiorniamo normalmente la sottocartella
              return { ...subfolder, ...updates };
            }
            return subfolder;
          });
          
          return {
            ...folder,
            subfolders: updatedSubfolders
          };
        }
        return folder;
      })
    };
  }
  
  // Cartella di primo livello
  return {
    ...state,
    folders: state.folders.map(folder => {
      if (folder.id === folderId) {
        console.log(`Aggiornamento cartella di primo livello: ${folder.name}`, updates);
        
        // Se ci sono documenti da rimuovere, li filtriamo
        if (updates.removeDocumentIds && Array.isArray(updates.removeDocumentIds) && updates.removeDocumentIds.length > 0) {
          const updatedDocumentIds = Array.isArray(folder.documentIds) ?
            folder.documentIds.filter(id => !updates.removeDocumentIds.includes(id)) :
            [];
          
          return {
            ...folder,
            ...updates,
            documentIds: updatedDocumentIds
          };
        }
        // Altrimenti, aggiorniamo normalmente la cartella
        return { ...folder, ...updates };
      }
      return folder;
    })
  };
};

// Reducer per gestire le azioni relative alle cartelle
const folderReducer = (state, action) => {
  switch (action.type) {
    case SELECT_DOCUMENT:
      return handleSelectDocument(state, action.payload);
    case SYNC_FOLDERS:
      return handleSyncFolders(state, action.payload);
    case REFRESH_FOLDERS:
      return handleRefreshFolders(state);
    case FREE_DOCUMENTS_FROM_FOLDER:
      // Se un documento viene liberato mentre il suo aggancio e' ancora in
      // volo, l'attesa va cancellata: altrimenti la prossima lettura delle
      // cartelle lo rimetterebbe dentro.
      return dropPendingLinks(
        handleFreeDocumentsFromFolder(state, action.payload),
        action.payload?.documentIds
      );
    case CREATE_FOLDER:
      return handleCreateFolder(state, action.payload);
    case DELETE_FOLDER:
      return handleDeleteFolder(state, action.payload);
    case ADD_DOCUMENT_TO_FOLDER:
      return handleAddDocumentToFolder(state, action.payload);
    case REMOVE_DOCUMENT_FROM_FOLDER:
      return dropPendingLinks(
        handleRemoveDocumentFromFolder(state, action.payload),
        action.payload?.documentId
      );
    case UPDATE_FOLDER:
      return handleUpdateFolder(state, action.payload);
    // Per gli altri casi relativi alle cartelle, restituisci null
    // per farli gestire temporaneamente dal reducer principale
    default:
      return null; // Indica che questa azione non è gestita da questo reducer
  }
};

export default folderReducer; 